import React, { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ChevronDown, ChevronRight, Link2, AlertTriangle } from "lucide-react";
import { LinkedChecklistScanEntry } from "../../lib/api-central-mu";

/**
 * Post-selection confirmation step: the selected checklists regrouped by whether they configure
 * a linked process (contain LINKED_CHECKLIST parameters). Linked ones will go through the
 * linked-resolution flow after the target step; standard ones go through the plain import.
 * Display-only — resolution decisions stay in LinkedResolutionFlow.
 */
interface Props {
  entries: LinkedChecklistScanEntry[];
  /** Selected checklists, for name/code fallback when the scan couldn't name an entry. */
  checklists: { id: string; name: string; code?: string }[];
  onBack: () => void;
  onContinue: () => void;
}

const ChecklistScanGroups: React.FC<Props> = ({ entries, checklists, onBack, onContinue }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const nameOf = (entry: LinkedChecklistScanEntry) => {
    if (entry.checklistName) return entry.checklistName;
    const selected = checklists.find((c) => c.id === entry.checklistId);
    return selected ? selected.name : entry.checklistId;
  };
  const codeOf = (entry: LinkedChecklistScanEntry) =>
    checklists.find((c) => c.id === entry.checklistId)?.code;

  const linked = entries.filter((e) => e.hasLinkedProcess);
  const standard = entries.filter((e) => !e.hasLinkedProcess && e.foundInExport);
  const notFound = entries.filter((e) => !e.foundInExport);
  const singleSelection = entries.length === 1;

  const row = (entry: LinkedChecklistScanEntry, withChildren: boolean) => (
    <li key={entry.checklistId} className="py-2 px-3">
      <div className="flex items-center gap-2">
        {withChildren && entry.children.length > 0 && (
          <button
            type="button"
            aria-label="Toggle linked children"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((s) => ({ ...s, [entry.checklistId]: !s[entry.checklistId] }))}
          >
            {expanded[entry.checklistId] ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        <span className="font-medium">{nameOf(entry)}</span>
        {codeOf(entry) && (
          <span className="text-xs font-mono text-muted-foreground">{codeOf(entry)}</span>
        )}
        {entry.hasLinkedProcess && (
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
            <Link2 className="h-3 w-3 mr-1" />
            Linked process
          </Badge>
        )}
      </div>
      {withChildren && expanded[entry.checklistId] && entry.children.length > 0 && (
        <ul className="mt-1 ml-7 space-y-0.5">
          {entry.children.map((child) => (
            <li key={child.id} className="text-sm text-muted-foreground">
              ↳ {child.name || child.id}
              {child.code ? <span className="font-mono text-xs"> · {child.code}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-2">
        {singleSelection ? "Selected process" : "Review your selection"}
      </h2>
      <p className="text-muted-foreground mb-6">
        {linked.length > 0
          ? "Checklists that configure a linked process bring their linked children along — they go through the link-resolution step after you pick the target. Standard checklists migrate via the regular import."
          : "No linked processes detected — the selected checklist(s) will migrate via the standard import."}
      </p>

      {notFound.length > 0 && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2 font-medium text-red-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Not found in the source export
          </div>
          <p className="mt-1 text-sm text-red-700">
            Only PUBLISHED checklists export — the following selections were not returned by the
            source and will not migrate. Go back and deselect them.
          </p>
          <ul className="mt-1 text-sm text-red-800">
            {notFound.map((e) => (
              <li key={e.checklistId}>{nameOf(e)}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {linked.length > 0 && (
          <div className="rounded-md border">
            <div className="px-3 py-2 border-b bg-muted/50 font-medium">
              Linked process checklists ({linked.length})
            </div>
            <ul className="divide-y">{linked.map((e) => row(e, true))}</ul>
          </div>
        )}
        {standard.length > 0 && (
          <div className="rounded-md border">
            <div className="px-3 py-2 border-b bg-muted/50 font-medium">
              Standard checklists ({standard.length})
            </div>
            <ul className="divide-y">{standard.map((e) => row(e, false))}</ul>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-end space-x-4">
        <Button onClick={onBack} variant="outline" className="px-6">
          Back
        </Button>
        <Button onClick={onContinue} variant="default" className="px-6">
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ChecklistScanGroups;
